<template>
    <div class="tw-my-8">
        <form
            class="tw-p-0"
            @submit.prevent="validateBeforeSubmit"
        >
            <div class="tw-grid md:tw-grid-cols-2">
                <div class="tw-flex tw-flex-col">
                    <TextInputComponent
                        :label="$t('personalInformation.firstName')"
                        name="firstName"
                        :value="firstName"
                        @change="updateField('firstName', $event.target.value)"
                    />
                    <span
                        class="tw-text-brand"
                        v-if="$v.firstName.$dirty && !$v.firstName.required"
                    >
                        {{ $t("errorMessages.missingFirstName") }}
                    </span>
                </div>
                <div class="tw-flex tw-flex-col">
                    <TextInputComponent
                        :label="$t('personalInformation.lastName')"
                        name="lastName"
                        :value="lastName"
                        @change="updateField('lastName', $event.target.value)"
                    />
                    <span
                        class="tw-text-brand"
                        v-if="$v.lastName.$dirty && !$v.lastName.required"
                    >
                        {{ $t("errorMessages.missingLastName") }}
                    </span>
                </div>
            </div>
            <div class="tw-flex tw-flex-col">
                <TextInputComponent
                    :label="$t('personalInformation.email')"
                    name="email"
                    type="email"
                    :value="email"
                    @change="updateField('email', $event.target.value)"
                />
                <span
                    class="tw-text-brand"
                    v-if="$v.email.$dirty && !$v.email.required"
                >
                    {{ $t("errorMessages.missingEmail") }}
                </span>
                <span
                    class="tw-text-brand"
                    v-if="$v.email.$dirty && !$v.email.email"
                >
                    {{ $t("errorMessages.incorrectEmail") }}
                </span>
            </div>
            <div v-if="inquiryType === 'phone-inquiry'" class="tw-flex tw-flex-col">
                <PhoneInputComponent
                    :label="$t('personalInformation.phone')"
                    name="phone"
                    :value="phone"
                    @change="updateField('phone', $event)"
                />
                <span
                    class="tw-text-brand"
                    v-if="$v.phone.$dirty && !$v.phone.required"
                >
                    {{ $t("errorMessages.missingPhone") }}
                </span>
                <span
                    class="tw-text-brand"
                    v-if="$v.phone.$dirty && !$v.phone.onlyNumbers"
                >
                    {{ $t("errorMessages.invalidPhone") }}
                </span>
            </div>
            <div class="tw-flex tw-items-baseline tw-gap-2 tw-my-4">
                <input
                    class="tw-accent-red-500"
                    type="checkbox"
                    name="mailchimpSignup"
                    id="mailchimpSignup"
                    :value="mailchimpSignup"
                    @change="updateField('mailchimpSignup', $event?.target?.checked)"
                />
                <label
                    class="tw-w-full tw-block"
                    for="mailchimpSignup"
                    v-html="$t('personalInformation.mailchimpSignup')"
                />
            </div>
            <div class="tw-flex tw-flex-col">
                <div class="tw-flex tw-items-baseline tw-gap-2 tw-my-4 lg:tw-mb-20">
                    <input
                        class="tw-accent-red-500"
                        type="checkbox"
                        name="tos"
                        id="tos"
                        :value="acceptToS"
                        @change="updateField('acceptToS', $event?.target?.checked)"
                    />
                    <label
                        class="tw-w-full tw-block"
                        for="tos"
                        v-html="$t('personalInformation.agbConfirmation')"
                    />
                </div>
                <span
                    class="tw-text-brand tw-block tw-mt-3"
                    v-if="$v.acceptToS.$dirty && !$v.acceptToS.required"
                >
                    {{ $t("errorMessages.tosNotAccepted") }}
                </span>
            </div>
        </form>
    </div>
</template>

<script>
import TextInputComponent from "../UI/TextInputComponent.vue"
import PhoneInputComponent from "../UI/PhoneInputComponent.vue"
import {validationMixin} from "vuelidate";
import {email, required} from "vuelidate/lib/validators";
import axios from "axios";

const onlyNumbers = (value) => /^\d+$/.test(value);

export default {
    name: "CTAFields",
    components: {TextInputComponent, PhoneInputComponent},
    mixins: [validationMixin],
    props: {
        inquiryType: {
            type: String,
        }
    },
    data: () => ({
        loading: false,
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        acceptToS: false,
        mailchimpSignup: false,
        emailIsValid: false,
        phoneValid: false,
    }),
    validations: {
        firstName: {
            required
        },
        lastName: {
            required
        },
        email: {
            required,
            email
        },
        phone: {
            required,
            onlyNumbers
        },
        acceptToS: {
            required
        },
        mailchimpSignup: {
        }
    },
    methods: {
        checkEmailDomain() {
            const email = this.email;
            const domain = email.substring(email.lastIndexOf("@") + 1);

            axios.post(`/api/check-domain?domain=${domain}`, {
                domain: domain
            }, {headers: {'Content-Type': 'application/json'}})
                .then((response) => {
                    this.emailIsValid = !!response.data.exists;
                })
                .catch(error => {
                    console.error('Error:', error);
                })
                .finally(() => {
                    this.$v.email.$touch();
                });
        },
        validateBeforeSubmit() {
            this.$v.$touch()
            if (!this.$v.$invalid) {
                this.$emit("selected")
            }
        },
        updateField(fieldName, value) {
            this.$v[fieldName].$touch()
            this[fieldName] = value
            switch (fieldName) {
                case 'firstName':
                    this.$store.commit('tour/setFirstName', value)
                    break;
                case 'lastName':
                    this.$store.commit('tour/setLastName', value)
                    break;
                case 'email':
                    this.$store.commit('tour/setEmail', value)
                    this.$store.commit('tour/setEmailValid', !this.$v.email.$invalid)
                    break;
                case 'phone':
                    this.$store.commit('tour/setPhone', value)
                    this.phoneValid = !this.$v.phone.$invalid
                    this.$store.commit('tour/setPhoneValid', this.phoneValid)
                    this.$v.phone.$touch()
                    break;
                case 'acceptToS':
                    this.$store.commit('tour/setToS', value)
                    break;
                case 'mailchimpSignup':
                    this.$store.commit('tour/setMailchimpSignup', value)
                    break;
            }
        },
    },
}
</script>